import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { IUser, MessageType, NewsInfo } from "../interfaces/App";
import { addUser, delUser, getUser, updUser } from "../services/apiService";
import Button from "./Button";
import { IconType } from "./Icon";
import { setMessage, setSubTitle } from "../stores/AppReducer";
import CustomCheckBox from "./CustomCheckBox";
import ChatEdit from "./ChatEdit";
import type { AppDispatch, IApplicationState } from "../stores/AppStore";
import AppSetup from "../core/AppSetup";

export interface IExUser extends IUser {
    adminId: number;
};

function User() {
    const dispatch = useDispatch<AppDispatch>();
    const app = useSelector((appState: IApplicationState) => appState.app);
    const initialUser: IUser = {
        id: null,
        name: "",
        email: "",
        phone: "",
        rights: "", 
        readonly: false,
        token: "",
        hasToken: false,
        newsInfo: 0
    };
    const [localUser, setUser] = useState(initialUser);

    useEffect(() => {
        dispatch(getUser(app.user.id));
        dispatch(setSubTitle("User"));
        setUser({...initialUser});
    }, []);

    const setOnOff = (right: string) => (onOff: boolean) => {
        let tmpRights = localUser.rights?.split(",");
        tmpRights = tmpRights ? tmpRights : [];
        if (onOff) {
            tmpRights.push(right);
        } else {
            tmpRights = tmpRights.filter((r: string) => r !== right);
        }
        setUser({...localUser, rights: tmpRights.join(',')});
    }
    const saveUser = (ev: any) => {
        if (localUser.id) {
            dispatch(updUser({...localUser, adminId: app.user.id}));
        } else {
            dispatch(addUser({...localUser, adminId: app.user.id, rights: localUser.rights ? localUser.rights : ""}));
            setUser(initialUser);
        }
        ev.preventDefault();
    };
    const deleteUser = (id: string) => {
        dispatch(delUser({id, adminId: app.user.id})).then((res: any) => {
            if (res.type === "delete/User/fulfilled") {
                dispatch(setMessage({msg: "Benutzer gelöscht", type: MessageType.success}));
                dispatch(getUser(app.user.id));
            }
        });
    };
    const editUser = (email: string) => {
        setUser(app.users.find((u: IUser) => u.email === email));
    };
    const renderRightCheckBox = (right: string, idx: number) => {
        const isOn = localUser.rights?.split(",").findIndex((r: string) => r === right) > -1;
        return (
            <CustomCheckBox key={"right_" + idx} className="" value={isOn} label={right} switch={() => setOnOff(right)(!isOn)}/>
        );
    };
    const renderRightBox = () => {
        return (
            <div className="rights">
                {app.settings?.rights?.map(renderRightCheckBox)}
            </div>
        );
    };
    const renderUserDialog = () => {
        const newsInfo = localUser.newsInfo === NewsInfo.push 
            ? "push"
            : localUser.newsInfo === NewsInfo.mail
                ? "email"
                    : localUser.newsInfo === NewsInfo.sms
                    ? "sms"
                    : "none"
        return (
            <form onSubmit={(ev: any) => saveUser(ev)} onReset={() => setUser(initialUser)}>
                <div key={"user_edit"} className="user_edit">
                    <label>Name</label>
                    <input value={localUser.name} onChange={(ev: any) => setUser({...localUser, name: ev.target.value})}/>
                    {renderRightBox()}
                    <label>E-Mail</label>
                    <input value={localUser.email} onChange={(ev: any) => setUser({...localUser, email: ev.target.value})}/>
                    
                    <label>Telefon</label>
                    <input value={localUser.phone} onChange={(ev: any) => setUser({...localUser, phone: ev.target.value})}/>

                    <label>Rechte</label>
                    <input value={localUser.rights} onChange={(ev: any) => setUser({...localUser, rights: ev.target.value})}/>

                    <label>Token</label>
                    <input value={localUser.token} onChange={(ev: any) => setUser({...localUser, token: ev.target.value})}/>

                    <label>News Info</label>
                    <input value={newsInfo} readOnly/>
                </div>
                <input type="submit" value={localUser.id ? "Speichern" : "Anlegen"} />
                <input type="reset" value={"Reset"} />
            </form>
        );
    };
    const renderUserEditList = (user: IUser, idx: number) => {
        return (
            <div key={"user_" + idx} className={"user_list"}>
                <div title={user.id}>{user.name}</div>
                <div>{user.email}</div>
                <div>{user.register ? user.register.toLocaleDateString() + " " + user.register.toLocaleTimeString() : "invited"}</div>
                <div>{user.lastLogin ? user.lastLogin.toLocaleDateString() + " " + user.lastLogin.toLocaleTimeString() : ""}</div>
                <div>{user.rights}</div>
                <div>
                    <Button onClick={() => deleteUser(user.id)} icon={IconType.trash} className="userIcon"/>
                    <Button onClick={() => editUser(user.email)} icon={IconType.pencil} className="userIcon"/>
                </div>
            </div>
        );
    };
    const renderUserStatusLinkList = (user: IUser) => {
        const link = AppSetup.getServerUrl() + "#/status/:" + user.token;
        return user.token ? (
            <div key={"link_list_" + user.id} className={"link_list"}>
                {user.name}:<a href={link} target="blank">{link}</a>
            </div>
        ) : (null);
    };
    return (
        <div className="page">
            <fieldset>
                <legend>Users</legend>
                <div key={"user_head"} className={"user_list"}>
                    <div>Name</div>
                    <div>Email</div>
                    <div>Registered</div>
                    <div>Last login</div>
                    <div>Rights</div>
                    <div/>
                </div>
                {app.users.length && app.users.map(renderUserEditList)}
                {renderUserDialog()}
            </fieldset>
            <fieldset>
                <legend>Öffentliche Links</legend>
                {app.users.length && app.users.map(renderUserStatusLinkList)}
            </fieldset>
            <fieldset>
                <legend>Chats</legend>
                <ChatEdit />
            </fieldset>
        </div>
    );
}
export default User;
